'use client'

import { MemberSubmission, RoundDetails } from '@/types'
import { format, isAfter, isFuture } from 'date-fns'
import { Button, Spinner } from 'flowbite-react'
import { ExclamationCircle, FaceGrin } from 'flowbite-react-icons/outline'
import Link from 'next/link'
import useSWR from 'swr'
import { CtaPanel } from '../CtaPanel'
import { FilePen, Rocket } from 'flowbite-react-icons/solid'
import { ChallengeTheme } from '@/types/Event'

const fetcher = (url: string) =>
  fetch(url).then((res) => {
    if (!res.ok) {
      if (res.status === 404) {
        return undefined
      }

      const error = new Error('An error occurred while fetching the data.')
      throw error
    }

    return res.json()
  })

export interface MySubmissionSummaryProps {
  isTeamLeader: boolean
  eventSlug: string
  themes: ChallengeTheme[]
  round: RoundDetails
}

export const MySubmissionSummary = ({
  isTeamLeader,
  eventSlug,
  themes,
  round,
}: MySubmissionSummaryProps) => {
  const submissionForm = round.submission_form
  const {
    data: submission,
    error,
    isLoading,
  } = useSWR<MemberSubmission>(
    `/web/events/${eventSlug}/submissions?roundId=${round.id}`,
    fetcher
  )

  if (error) return 'An error has occurred.'
  if (isLoading) return <Spinner />

  const eventsUrl = '/web/events'
  const submissionUrl = `/web/events/${eventSlug}/my-submission`

  if (!submissionForm || submissionForm.status === 'draft') {
    return (
      <CtaPanel
        className="border border-gray-200 rounded-lg"
        header="Submissions should be open soon, we're just encountering a few
      hiccups at the moment. 😅"
        subHeader="In the meantime, feel free to explore other hackathons or connect
      with fellow participants. Thank you for your patience!"
        cta={{ text: 'Explore Hackathons', url: eventsUrl }}
      ></CtaPanel>
    )
  }

  const isAcceptSubmission = isFuture(round.submission_deadline)
  const selectedThemeIndex = submission
    ? themes.findIndex((theme) => theme.id === submission.challenge_theme_id)
    : -1
  const selectedTheme =
    selectedThemeIndex >= 0
      ? `Challenge Theme ${
          selectedThemeIndex + 1
        }: ${themes[selectedThemeIndex].theme}`
      : undefined

  if (isAcceptSubmission) {
    if (submission) {
      return (
        <div className="flex flex-col p-6 gap-4 border border-gray-200 rounded-lg md:flex-row md:items-center">
          <div className="space-y-4">
            <h3
              className={`text-2xl font-bold ${
                submission.status === 'draft'
                  ? 'text-gray-400'
                  : 'text-gray-700'
              }`}
            >
              {submission.status === 'draft' ? 'Draft' : 'Submitted'}
            </h3>
            <div className="space-y-1 text-gray-900 text-sm">
              <p className="flex items-center gap-2">
                <Rocket className="w-4 h-4" /> {selectedTheme}
              </p>
              {submission.updated_at && (
                <p className="flex items-center gap-2">
                  <FilePen className="w-4 h-4" /> Last Updated{' '}
                  {format(submission.updated_at, 'dd MMM yyyy, HH:mm')}
                </p>
              )}
            </div>
          </div>
          <Button
            color="dark"
            as={Link}
            href={submissionUrl}
            className="w-full px-5 md:ml-auto md:w-auto"
          >
            {isTeamLeader && submission.status === 'draft' ? 'Edit' : 'View'}
          </Button>
        </div>
      )
    } else {
      if (isTeamLeader) {
        return (
          <CtaPanel
            className="border border-gray-200 rounded-lg"
            header="Start crafting your winning entry now! 🤩"
            subHeader="Your journey to victory begins here! We can't wait to see your innovation, so dive in and begin crafting your masterpiece."
            cta={{ text: 'Create Submission', url: submissionUrl }}
          ></CtaPanel>
        )
      } else {
        return (
          <div className="p-4 bg-gray-50 text-gray-800 space-y-1.5">
            <p className="font-semibold flex items-center gap-2">
              <ExclamationCircle className="w-5 h-5" />
              Your team has yet to create a submission.{' '}
            </p>
            <p className="text-gray-900 text-sm">
              Once your team leader has create a submission, you will be able to
              view the details here.
            </p>
          </div>
        )
      }
    }
  } else {
    if (submission) {
      const isSubmittedAfterDeadline =
        submission.updated_at &&
        isAfter(submission.updated_at, round.submission_deadline)
      return (
        <>
          {isSubmittedAfterDeadline && submission.status === 'draft' && (
            <div className="p-4 mb-10 bg-gray-50 text-gray-800 space-y-1.5">
              <p className="font-semibold flex items-center gap-2">
                <FaceGrin className="w-5 h-5" />
                Sorry, the submission deadline for this hackathon has passed.
              </p>
              <p className="text-gray-900 text-sm">
                Unfortunately, your entry was not received within the deadline.
                We appreciate your interest and effort nonetheless, and we
                encourage you to continue exploring opportunities for innovation
                with us in the future!
              </p>
            </div>
          )}
          <div className="flex flex-col p-6 gap-4 border border-gray-200 rounded-lg md:flex-row md:items-center">
            <div className="space-y-4">
              <h3 className="text-gray-700 text-2xl font-bold">
                {submission.status === 'draft'
                  ? 'Failed to submit'
                  : 'Submitted'}
              </h3>
              <div className="space-y-1 text-gray-900 text-sm">
                <p className="flex items-center gap-2">
                  <Rocket className="w-4 h-4" /> {selectedTheme}
                </p>
                {submission.updated_at && (
                  <p className="flex items-center gap-2">
                    <FilePen className="w-4 h-4" /> Last Updated{' '}
                    {format(submission.updated_at, 'dd MMM yyyy, HH:mm')}
                  </p>
                )}
              </div>
            </div>
            <Button
              color="light"
              as={Link}
              href={submissionUrl}
              className="w-full px-5 md:ml-auto md:w-auto"
            >
              View Submission
            </Button>
          </div>
        </>
      )
    } else {
      return (
        <CtaPanel
          className="border border-gray-200 rounded-lg"
          header="Submission for this hackathon has ended."
          subHeader="We're sorry it did not work out this time. We appreciate your interest, and we encourage you to explore more opportunities for innovation with us in the future!"
          cta={{ text: 'Explore Hackathons', url: eventsUrl }}
        ></CtaPanel>
      )
    }
  }
}
